import { signOut } from 'next-auth/react'
import { createClient } from 'graphql-ws'
import { ApolloClient, ApolloLink, HttpLink, InMemoryCache, split } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { getMainDefinition } from '@apollo/client/utilities'
import logger from '~/utils/logger'

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach((error) => {
      const { extensions, message, locations, path } = error
      const originalError: any = extensions?.originalError
      const code = extensions?.code || originalError?.extensions?.code

      if (code === 'TOKEN_EXPIRED' || code === 'TOKEN_NOT_VERIFIED' || code === 'INVALID_TOKEN') {
        signOut({ callbackUrl: '/' })
      }
      logger.error(`[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(locations)}, Path: ${path}`)
    })
  } else if (networkError) {
    logger.error(`[Network error]: ${networkError}`)
  }
})

const httpLink = new HttpLink({
  uri: `${process.env.NEXT_PUBLIC_APP_URL}/api/graphql`,
  credentials: 'include',
  fetchOptions: {
    timeout: 120000, // 120 seconds timeout
  },
})
const wsLink = new GraphQLWsLink(
  createClient({
    url: `wss://${process.env.NEXT_PUBLIC_APP_URL}/api/subscriptions`,
  }),
)

/**
 * @see https://www.apollographql.com/docs/react/data/subscriptions#3-split-communication-by-operation-recommended
 * The split function takes three parameters:
 * 1. A function that's called for each operation to execute
 * 2. The Link to use for an operation if the function returns a "truthy" value
 * 3. The Link to use for an operation if the function returns a "falsy" value
 */
const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query)
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription'
  },
  wsLink,
  httpLink,
)

const authLink = setContext((_, { headers }) => {
  return {
    headers,
  }
})

const operationNamingLink = new ApolloLink((operation, forward) => {
  operation.setContext(() => ({
    uri: `${process.env.NEXT_PUBLIC_APP_URL}/api/graphql?${operation.operationName}`,
  }))
  return forward(operation)
})

const apolloClient = new ApolloClient({
  link: ApolloLink.from([errorLink, authLink, operationNamingLink, splitLink]),
  cache: new InMemoryCache(),
})

export default apolloClient
